import { useEffect } from "react";
import { GamePageContainer, GamePageContainerProps } from "./GamePageContainer";
import { useStore as useAttachStoreApi } from "./attach-store";
import { useGameStore } from "./store";
import { GameInsightsSettingsProps, GamePageLazyResponse, GamePageResponse, GamePlayerStatResponse } from "./types";

interface UseGamePageProps extends Omit<GamePageContainerProps, "gameData" | "gameDataLazy"> {
	gameData: GamePageResponse | undefined;
	gameDataLazy: GamePageLazyResponse | undefined;
	gameInsightsSettings: GameInsightsSettingsProps;
	gamePlayerStat?: GamePlayerStatResponse | null;
	gamePlayerStatIsLoading?: boolean;
}
export function useGamePage({
	toastMessage,
	isAdmin,
	showShareButton,
	domainURL,
	gameData,
	gameDataLazy,
	locale,
	currentNavOption,
	favoriteDisabled,
	clips,
	gameEventFilters,
	isFavorite,
	handleChangeColors,
	handleChangeScore,
	onFavoriteClick,
	handleChangeNavOptions,
	handleTeamSheetEdit,
	handleGameStatsExport,
	handleGameEventDataExport,
	loadCompositionPlayers,
	searchPlayers,
	loadClubPlayers,
	highlightsHandlers,
	handleLeftSideStart,
	handleChangeDescription,
	handleChangeAnalysisApproval,
	handleAbortGameResync,
	handleGameResync,
	canSwapHomeAwayMapping,
	isSwappingHomeAwayMapping,
	handleSwapHomeAwayMapping,
	swapped,
	keepMappingAllowed,
	handleTeamSheetDataExport,
	handleSetPlayerRating,
	handleSetPlayerBadges,
	handleSubmitPlayerGrading,
	gameEventComment,
	handleReportError,
	gameInsightsSettings,
	showCoachToolbar,
	storeDownloadVideoActivity,
	physicalAnalysisReport,
	renderAIGameSummary,
	gamePlayerStat,
	coachReviewPromises,
	gamePlayerStatIsLoading,
	extraAdminOptions,
	userId,
	activeContextTeamId,
	backLink,
	editGameHref,
	editGameLabel,
	allowEditDescription,
	showLocation,
}: UseGamePageProps) {
	const { setGameData } = useGameStore();
	const gameId = gameData?.game.id;

	useEffect(() => {
		if (!gameData) {
			return;
		}
		setGameData(gameData, gameDataLazy);
		useAttachStoreApi.getState().refreshAttachLock();
	}, [gameData, gameDataLazy, setGameData]);

	useEffect(() => {
		if (gameId == null) {
			return;
		}

		return () => {
			useAttachStoreApi.getState().reset();
		};
	}, [gameId]);

	if (!gameData) {
		return null;
	}

	return (
		<GamePageContainer
			toastMessage={toastMessage}
			isAdmin={isAdmin}
			showShareButton={showShareButton}
			domainURL={domainURL}
			gameData={gameData}
			gameDataLazy={gameDataLazy}
			locale={locale}
			currentNavOption={currentNavOption}
			handleChangeColors={handleChangeColors}
			handleChangeScore={handleChangeScore}
			handleChangeNavOptions={handleChangeNavOptions}
			highlightsHandlers={highlightsHandlers}
			handleLeftSideStart={handleLeftSideStart}
			handleGameStatsExport={handleGameStatsExport}
			handleGameEventDataExport={handleGameEventDataExport}
			onFavoriteClick={onFavoriteClick}
			favoriteDisabled={favoriteDisabled}
			isFavorite={isFavorite}
			clips={clips}
			gameEventFilters={gameEventFilters}
			handleTeamSheetEdit={handleTeamSheetEdit}
			loadCompositionPlayers={loadCompositionPlayers}
			searchPlayers={searchPlayers}
			loadClubPlayers={loadClubPlayers}
			handleChangeDescription={handleChangeDescription}
			handleChangeAnalysisApproval={handleChangeAnalysisApproval}
			handleAbortGameResync={handleAbortGameResync}
			handleGameResync={handleGameResync}
			canSwapHomeAwayMapping={canSwapHomeAwayMapping}
			isSwappingHomeAwayMapping={isSwappingHomeAwayMapping}
			handleSwapHomeAwayMapping={handleSwapHomeAwayMapping}
			swapped={swapped}
			keepMappingAllowed={keepMappingAllowed}
			handleTeamSheetDataExport={handleTeamSheetDataExport}
			handleSetPlayerRating={handleSetPlayerRating}
			handleSetPlayerBadges={handleSetPlayerBadges}
			handleSubmitPlayerGrading={handleSubmitPlayerGrading}
			gameEventComment={gameEventComment}
			handleReportError={handleReportError}
			gameInsightsSettings={gameInsightsSettings}
			showCoachToolbar={showCoachToolbar}
			storeDownloadVideoActivity={storeDownloadVideoActivity}
			physicalAnalysisReport={physicalAnalysisReport}
			renderAIGameSummary={renderAIGameSummary}
			gamePlayerStat={gamePlayerStat}
			gamePlayerStatIsLoading={gamePlayerStatIsLoading}
			extraAdminOptions={extraAdminOptions}
			coachReviewPromises={coachReviewPromises}
			userId={userId}
			activeContextTeamId={activeContextTeamId}
			backLink={backLink}
			editGameHref={editGameHref}
			editGameLabel={editGameLabel}
			allowEditDescription={allowEditDescription}
			showLocation={showLocation}
		/>
	);
}
